// Search-result card. Tamang form is the hero; Nepali and English are secondary.
// Clicking the card navigates to the full entry page; vote buttons stop propagation.

import { useNavigate } from 'react-router-dom'
import type { EntrySearchRow } from '../types'
import { StatusBadge, TypeBadge, EvidenceChips } from './EvidenceBadges'
import { CardVoteButtons } from './VoteButtons'
import type { VoteCounts, VoteValue } from '../hooks/useVotes'

interface EntryCardProps {
  entry:     EntrySearchRow
  counts:    VoteCounts | undefined
  myVote:    VoteValue
  castVote:  (entryId: string, v: 'up' | 'down') => Promise<void>
}

export function EntryCard({ entry, counts, myVote, castVote }: EntryCardProps) {
  const navigate = useNavigate()

  return (
    <article
      className="dict-card group p-5 space-y-3"
      onClick={() => navigate(`/entry/${entry.entry_id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter') navigate(`/entry/${entry.entry_id}`) }}
      aria-label={`Entry: ${entry.tamang_form_surface} — ${entry.nepali_form}`}
    >
      {/* Top row: Tamang form + badges */}
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <p className="deva text-2xl font-bold text-tamang-700 leading-tight">
            {entry.tamang_form_surface}
          </p>
          {entry.tamang_form_normalized &&
           entry.tamang_form_normalized !== entry.tamang_form_surface && (
            <p className="deva text-sm text-tamang-600 opacity-70 mt-0.5">
              ({entry.tamang_form_normalized})
            </p>
          )}
        </div>

        <div className="flex flex-wrap gap-1.5 shrink-0 mt-1">
          <span className="badge-candidate">CANDIDATE</span>
          <StatusBadge status={entry.status} />
          <TypeBadge   type={entry.entry_type} />
        </div>
      </div>

      {/* Romanized Tamang */}
      {entry.romanized_tamang && (
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide shrink-0">
            ROM
          </span>
          <span className="text-stone-600 font-medium tracking-wide">{entry.romanized_tamang}</span>
        </div>
      )}

      {/* Nepali equivalent */}
      <div className="flex items-baseline gap-2">
        <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide shrink-0">
          Nepali
        </span>
        <span className="deva text-lg text-stone-700 font-medium">{entry.nepali_form}</span>
      </div>

      {/* English gloss */}
      {entry.english_gloss_auto && (
        <div className="flex items-baseline gap-2">
          <span className="text-xs text-stone-400 font-semibold uppercase tracking-wide shrink-0">
            English
          </span>
          <span className="text-stone-500 italic text-sm">{entry.english_gloss_auto}</span>
          <span className="badge-auto">auto</span>
        </div>
      )}

      {/* See more */}
      <div className="flex items-center justify-between gap-2">
        <CardVoteButtons
          entryId={entry.entry_id}
          counts={counts}
          myVote={myVote}
          castVote={castVote}
        />
        <span className="text-xs font-medium text-tamang-600 group-hover:text-tamang-800 flex items-center gap-1 shrink-0">
          Examples &amp; details
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </div>

      {/* Footer: evidence + frequency */}
      <div className="flex items-center justify-between gap-2 pt-1 border-t border-stone-100">
        <EvidenceChips entry={entry} />
        <span className="text-xs text-stone-400 shrink-0">
          freq {entry.frequency.toLocaleString()}
        </span>
      </div>
    </article>
  )
}
